import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API } from "./shared";
import { StaffPanel } from "./PlayersTab";

const inp = "mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm";
const lbl = "text-[11px] font-bold uppercase tracking-wider text-white/40";
const lines = (a) => (a || []).join("\n");
const unlines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

function CategoryForm({ c, token, onSaved }) {
  const [f, setF] = useState({ ...c, focus: lines(c.focus), age_divisions: lines(c.age_divisions) });
  const [state, setState] = useState("");
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setState("saving");
    try {
      await axios.put(`${API}/admin/categories/${c.key}`, { ...f, focus: unlines(f.focus), age_divisions: unlines(f.age_divisions) }, { headers: { "X-Admin-Token": token } });
      setState("saved");
      onSaved();
    } catch (e) {
      setState(e?.response?.data?.detail || "error");
    }
  };
  const field = (k, label, props = {}) => <label className="block"><span className={lbl}>{label}</span><input data-testid={`cat-${c.key}-${k}`} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} className={inp} {...props} /></label>;
  const area = (k, label, rows = 3) => <label className="block md:col-span-2"><span className={lbl}>{label}</span><textarea data-testid={`cat-${c.key}-${k}`} rows={rows} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} className={inp} /></label>;
  return (
    <div data-testid={`category-settings-${c.key}`} className="rounded-2xl bg-white/5 border border-white/10 p-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {field("name", "Category name")}
        {field("display_order", "Display order", { type: "number" })}
        {area("short_description", "Short description (homepage card)", 2)}
        {area("full_description", "Full description (category page)")}
        {area("focus", "Development focus (one per line)")}
        {area("age_divisions", "Default age divisions (one per line)", 2)}
        {field("default_location", "Default facility")}
        {field("default_session_minutes", "Default session minutes", { type: "number" })}
        <label className="flex items-center gap-2 text-sm font-semibold text-white/80"><input type="checkbox" data-testid={`cat-${c.key}-active`} checked={f.active !== false} onChange={(e) => set("active", e.target.checked)} className="accent-[#2563EB]" /> Shown on website</label>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button data-testid={`cat-${c.key}-save`} onClick={save} className="rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-bold text-white">Save {c.name}</button>
        {state && state !== "saving" && <span className={`text-xs font-bold ${state === "saved" ? "text-emerald-300" : "text-red-300"}`}>{state === "saved" ? "Saved" : state}</span>}
      </div>
    </div>
  );
}

export default function CategoriesTab({ ctx }) {
  const [cats, setCats] = useState(null);
  const load = () => axios.get(`${API}/admin/categories`, { headers: { "X-Admin-Token": ctx.token } }).then((r) => setCats(r.data.categories));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">Category Settings</h1>
      <p className="mt-2 text-sm font-medium text-white/50">Customer-facing content for the four permanent categories. New offerings start from these defaults; editing here never changes existing offerings or past registrations.</p>
      {!cats && <Loader2 className="mt-6 animate-spin text-white/40" />}
      <div className="mt-6 space-y-4">{(cats || []).map((c) => <CategoryForm key={c.key} c={c} token={ctx.token} onSaved={load} />)}</div>
      <h2 className="mt-12 text-sm font-extrabold uppercase tracking-wider text-white/50">Staff access</h2>
      <div className="mt-3"><StaffPanel token={ctx.token} /></div>
    </>
  );
}
